import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { PERMISSION_LEVELS } from "./roleDisplay";

const groupCatalog = (catalog) => {
  const groups = [];
  const byId = new Map();

  (catalog || []).forEach((item) => {
    if (!byId.has(item.groupId)) {
      const group = { id: item.groupId, label: item.groupLabel, rows: [] };
      byId.set(item.groupId, group);
      groups.push(group);
    }

    byId.get(item.groupId).rows.push(item);
  });

  return groups;
};

const getLevelMeta = (value) => PERMISSION_LEVELS.find((level) => level.value === value) || PERMISSION_LEVELS[0];

const RolePermissionMatrix = ({
  catalog,
  levelsByCapability,
  canEditLocked,
  isSaving,
  isDirty,
  onLevelChange,
  onSave,
}) => {
  const groups = useMemo(() => groupCatalog(catalog), [catalog]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        mb={2}
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          {PERMISSION_LEVELS.map((level) => (
            <Chip
              key={level.value}
              size="small"
              label={`${level.short}  ${level.label}`}
              sx={{
                fontWeight: 600,
                color: level.color,
                borderColor: level.color,
                backgroundColor: level.background,
              }}
              variant="outlined"
            />
          ))}
        </Stack>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!isDirty || isSaving}
          aria-label="Save role permissions"
        >
          {isSaving ? "Saving..." : "Save permissions"}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        R = view, W = edit data, X = decisive action (submit, finalize, convert). On the same row, X includes W and R.
        Locked rows can only be changed with Administration_Extended.
      </Alert>

      {groups.map((group) => (
        <Box key={group.id} sx={{ mb: 2.5 }}>
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              mb: 0,
              borderRadius: "8px 8px 0 0",
              bgcolor: "grey.100",
              border: "1px solid",
              borderColor: "divider",
              borderBottom: 0,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
              {group.label}
            </Typography>
          </Box>
          <TableContainer
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "0 0 8px 8px",
            }}
          >
            <Table size="small" aria-label={`${group.label} permissions`}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: "46%" }}>Capability</TableCell>
                  {PERMISSION_LEVELS.map((level) => (
                    <TableCell
                      key={level.value}
                      align="center"
                      sx={{ width: "13.5%", fontWeight: 700, color: level.color, px: 0.5 }}
                    >
                      {level.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {group.rows.map((row) => {
                  const locked = Boolean(row.locked);
                  const disabled = locked && !canEditLocked;
                  const value = levelsByCapability[row.id] || "—";
                  const selected = getLevelMeta(value);

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        backgroundColor: selected.background,
                        "& td": { borderColor: "divider" },
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {locked && (
                            <LockIcon fontSize="small" sx={{ color: "text.secondary" }} aria-label="Locked capability" />
                          )}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {PERMISSION_LEVELS.map((level) => {
                        const isChecked = value === level.value;

                        return (
                          <TableCell key={level.value} align="center" sx={{ py: 0.25, px: 0.5 }}>
                            <Radio
                              size="small"
                              checked={isChecked}
                              disabled={disabled}
                              onChange={() => {
                                if (locked && !canEditLocked) {
                                  return;
                                }
                                onLevelChange(row.id, level.value);
                              }}
                              inputProps={{ "aria-label": `${row.label} ${level.label}` }}
                              sx={{
                                color: level.color,
                                "&.Mui-checked": {
                                  color: level.color,
                                },
                              }}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default RolePermissionMatrix;
